import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { NoonReportTankCleaningSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getBoolValue, getNumberValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { ListHeaderStyle } from '../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { EngineListWrapper, EngineListRow, EngineListHeader, EngineListValuesWrapper } from '../engines-section/EnginesSection.style';
import { TankCleaningWrapper } from './TankCleaningSection.style';
import { roundTo2Decimals } from '../../../utils/numberHelper';
import { prettifyHours } from 'odfjell-date-time-utils/dateTimeFormatter';
import { useVesselStore } from 'src/context/VesselContext';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';

type TankCleaningVM = {
    tankCleaningOperationSinceLastReport?: boolean;
    tankCleaningDuration?: number;
    tankCleanerHeatersHoursWithHotWater?: number;
    tankCleanerHeatersHoursWithColdWater?: number;
    tankCleaningPumpFreshWaterRunningHours?: number;
    tankCleaningPumpSeaWaterRunningHours?: number;
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const alwaysShowTankPreparationContent = true; // change this to false (and test) when Product Owner agrees to use only Events data

const TankCleaningSectionComponent: React.FC = () => {
    const { selectedAolGroup, setSelectedAolDataItem, selectedAolDataItem, aolSyncEvent } = useAolStore();
    const { selectedVessel } = useVesselStore();
    const [tankCleaningVM, setTankCleaningVM] = useState<TankCleaningVM>({});
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const [tankCleaningChangedMessage, setTankCleaningChangedMessage] = useState<string>();
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.TankCleaning &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!selectedAolDataItem) return;

        if (!tankCleaningVM.isInitialized) {
            initVM();
        } else {
            updateTankCleaningChangedMessage(tankCleaningVM);
        }
    }, [
        selectedAolDataItem,
        selectedAolDataItem?.aolDataItemProperties.hasTankPreparationActivity,
        selectedAolDataItem?.aolDataItemProperties.tankCleaningHours,
    ]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(tankCleaningVM?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;

        const vm: TankCleaningVM = {
            tankCleaningOperationSinceLastReport: getBoolValue(noonReport?.tankCleaningSection?.tankCleaningOperationSinceLastReport) ?? false,
            tankCleaningDuration: getNumberValue(noonReport?.tankCleaningSection?.tankCleaningDuration, 2, true),
            tankCleanerHeatersHoursWithHotWater: getNumberValue(
                noonReport?.tankCleaningSection?.tankCleanerHeatersHoursWithHotWater,
                undefined,
                true
            ),
            tankCleanerHeatersHoursWithColdWater: getNumberValue(
                noonReport?.tankCleaningSection?.tankCleanerHeatersHoursWithColdWater,
                undefined,
                true
            ),
            tankCleaningPumpFreshWaterRunningHours: getNumberValue(
                noonReport?.tankCleaningSection?.tankCleaningPumpFreshWaterRunningHours,
                undefined,
                true
            ),
            tankCleaningPumpSeaWaterRunningHours: getNumberValue(
                noonReport?.tankCleaningSection?.tankCleaningPumpSeaWaterRunningHours,
                undefined,
                true
            ),
            sectionData: noonReport?.tankCleaningSection?.sectionData ?? createSectionData(NoonReportSectionEnum.TankCleaning),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        };

        updateTankCleaningChangedMessage(vm);

        setHasEmptyValues(false);
        setTankCleaningVM({ ...vm });
    };
    const setHasTankPreparationAolData = (hasTankPreparationAolData: boolean) => {
        if (!selectedAolDataItem) return;
        if (selectedAolDataItem.aolDataItemProperties.hasTankPreparationAolData !== hasTankPreparationAolData) {
            selectedAolDataItem.aolDataItemProperties.hasTankPreparationAolData = hasTankPreparationAolData;
            setSelectedAolDataItem({ ...selectedAolDataItem });
        }
    };

    const updateTankCleaningChangedMessage = (vm: TankCleaningVM) => {
        if (
            !selectedAolDataItem ||
            selectedAolDataItem?.aolDataItemProperties?.tankCleaningHours === undefined ||
            (selectedVessel && selectedVessel.hasOrcaWebSimpleMode)
        )
            return;

        const tankCleaningHours = selectedAolDataItem?.aolDataItemProperties?.tankCleaningHours;
        const hasTankPreparationActivity = selectedAolDataItem?.aolDataItemProperties?.hasTankPreparationActivity;
        setHasTankPreparationAolData(vm.tankCleaningOperationSinceLastReport ?? false);
        const noonReport = selectedAolDataItem.noonReport;

        if (roundTo2Decimals(vm.tankCleaningDuration) !== roundTo2Decimals(tankCleaningHours)) {
            if (noonReport?.tankCleaningSection) {
                setTankCleaningChangedMessage(
                    'Tank preparation duration has been updated in the reported events and is now ' +
                        prettifyHours(tankCleaningHours) +
                        ' instead of ' +
                        prettifyHours(vm.tankCleaningDuration) +
                        '. Please verify and save this AOL section.'
                );
            } else {
                setTankCleaningChangedMessage(undefined);
            }
            if (vm.tankCleaningOperationSinceLastReport !== hasTankPreparationActivity) {
                vm.tankCleaningOperationSinceLastReport = hasTankPreparationActivity;
            }
            if (vm.tankCleaningDuration === 0 && tankCleaningHours > 0) {
                vm.tankCleaningDuration = tankCleaningHours;
            }
        } else {
            setTankCleaningChangedMessage(undefined);
        }
        setTankCleaningVM({ ...vm });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }

        hasInvalidFields(tankCleaningVM, name, changeObject.isValid);
        setTankCleaningVM({ ...tankCleaningVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;

        if (changeObject.name === 'tankCleaningDuration') {
            changeObject.dataObject.tankCleaningOperationSinceLastReport = changeObject.hours > 0;
            setHasTankPreparationAolData(changeObject.dataObject.tankCleaningOperationSinceLastReport);
        }

        hasInvalidFields(changeObject.dataObject, changeObject.name, changeObject.isValid);
        setTankCleaningVM({ ...changeObject.dataObject });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const save = () => {
        const ignoreList = [''];

        if (!tankCleaningVM.tankCleaningOperationSinceLastReport) {
            ignoreList.push('tankCleaningDuration');
            ignoreList.push('tankCleanerHeatersHoursWithHotWater');
            ignoreList.push('tankCleanerHeatersHoursWithColdWater');
            ignoreList.push('tankCleaningPumpFreshWaterRunningHours');
            ignoreList.push('tankCleaningPumpSeaWaterRunningHours');
        }
        if (hasMissingValues(tankCleaningVM, ignoreList)) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportTankCleaningSectionFactory().createBM(tankCleaningVM);
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!tankCleaningVM.hasInvalidFields) {
                saveSection(data);
            }
        }
    };
    return (
        <>
            {tankCleaningVM && (
                <TankCleaningWrapper>
                    {tankCleaningChangedMessage && <ErrorText>{tankCleaningChangedMessage}</ErrorText>}
                    {!alwaysShowTankPreparationContent &&
                        tankCleaningVM.tankCleaningDuration !== undefined &&
                        tankCleaningVM.tankCleaningDuration > 0 && (
                            <OdfjellInputComponent
                                type={'duration'}
                                isReadOnly={true}
                                description={'Tank preparation'}
                                value={tankCleaningVM.tankCleaningDuration}
                                onChange={onInputChange}
                                hideIfEmpty={true}
                            />
                        )}
                    {alwaysShowTankPreparationContent && (
                        <OdfjellInputComponent
                            type={'time'}
                            isReadOnly={isReadOnly}
                            dataObject={tankCleaningVM}
                            name={'tankCleaningDuration'}
                            description={'Tank preparation'}
                            value={tankCleaningVM.tankCleaningDuration}
                            onTimeInputChange={onTimeInputChange}
                            min={0}
                            max={selectedAolGroup?.aolHours}
                        />
                    )}
                    <OdfjellInputComponent
                        type={'checkbox'}
                        isReadOnly={true}
                        description={'Tank preparation operation since last report'}
                        dataObject={tankCleaningVM}
                        name={'tankCleaningOperationSinceLastReport'}
                        value={tankCleaningVM.tankCleaningOperationSinceLastReport}
                        onChange={onInputChange}
                    />
                    {tankCleaningVM.tankCleaningOperationSinceLastReport && (
                        <>
                            <EngineListWrapper>
                                <ListHeaderStyle>
                                    <EngineListRow>
                                        <EngineListHeader className={'first-header-col'}>Tank cleaner heaters</EngineListHeader>
                                    </EngineListRow>
                                </ListHeaderStyle>
                                <EngineListRow>
                                    <div>Hours with cold water</div>
                                    <EngineListValuesWrapper gridCols={2}>
                                        <OdfjellInputComponent
                                            type={'time'}
                                            isReadOnly={isReadOnly}
                                            dataObject={tankCleaningVM}
                                            name={'tankCleanerHeatersHoursWithColdWater'}
                                            value={tankCleaningVM.tankCleanerHeatersHoursWithColdWater}
                                            onTimeInputChange={onTimeInputChange}
                                            min={0}
                                            max={selectedAolGroup?.aolHours}
                                        ></OdfjellInputComponent>
                                    </EngineListValuesWrapper>
                                </EngineListRow>
                                <EngineListRow>
                                    <div>Hours with hot water</div>
                                    <EngineListValuesWrapper gridCols={2}>
                                        <OdfjellInputComponent
                                            type={'time'}
                                            isReadOnly={isReadOnly}
                                            dataObject={tankCleaningVM}
                                            name={'tankCleanerHeatersHoursWithHotWater'}
                                            value={tankCleaningVM.tankCleanerHeatersHoursWithHotWater}
                                            onTimeInputChange={onTimeInputChange}
                                            min={0}
                                            max={selectedAolGroup?.aolHours}
                                        ></OdfjellInputComponent>
                                    </EngineListValuesWrapper>
                                </EngineListRow>
                            </EngineListWrapper>
                            <EngineListWrapper>
                                <ListHeaderStyle>
                                    <EngineListRow>
                                        <EngineListHeader className={'first-header-col'}>Tank cleaning pumps</EngineListHeader>
                                    </EngineListRow>
                                </ListHeaderStyle>
                                <EngineListRow>
                                    <div>Fresh water running hours</div>
                                    <EngineListValuesWrapper gridCols={2}>
                                        <OdfjellInputComponent
                                            type={'time'}
                                            isReadOnly={isReadOnly}
                                            dataObject={tankCleaningVM}
                                            name={'tankCleaningPumpFreshWaterRunningHours'}
                                            value={tankCleaningVM.tankCleaningPumpFreshWaterRunningHours}
                                            onTimeInputChange={onTimeInputChange}
                                            min={0}
                                            max={selectedAolGroup?.aolHours}
                                        ></OdfjellInputComponent>
                                    </EngineListValuesWrapper>
                                </EngineListRow>
                                <EngineListRow>
                                    <div>Sea water running hours</div>
                                    <EngineListValuesWrapper gridCols={2}>
                                        <OdfjellInputComponent
                                            type={'time'}
                                            isReadOnly={isReadOnly}
                                            dataObject={tankCleaningVM}
                                            name={'tankCleaningPumpSeaWaterRunningHours'}
                                            value={tankCleaningVM.tankCleaningPumpSeaWaterRunningHours}
                                            onTimeInputChange={onTimeInputChange}
                                            min={0}
                                            max={selectedAolGroup?.aolHours}
                                        ></OdfjellInputComponent>
                                    </EngineListValuesWrapper>
                                </EngineListRow>
                            </EngineListWrapper>
                        </>
                    )}
                </TankCleaningWrapper>
            )}
            {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
        </>
    );
};

export default TankCleaningSectionComponent;
